/**
 * シナリオダウンロードの公開エントリポイント。
 *
 * 素材一式 ZIP (シナリオ末尾の「ファイルDL」)。
 */
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import type { ProgressCallback, ScenarioDownloadData } from './types';
import { fetchMaterialBlob, getNarrationPrefix } from './assets';

/**
 * ダウンロード ZIP に同梱する `_scenario.json` の中身を組み立てる。
 * 「クリップボードにコピー」でも同じ内容を使うため、単一の正本として切り出している。
 * 生成プロンプト等の内部フィールドは含めず、基本要素と素材IDのみで構成する。
 */
export function buildScenarioExportJson(data: ScenarioDownloadData) {
  return {
    id: data.scenarioId,
    title: data.title,
    summary: data.summary,
    // scenes 外のシナリオ単位アセット。BGM とストーリーボード絵コンテ画像
    // (ストーリーボードモードで選択中の 1 枚) をトップレベルで参照する。
    bgmMaterialId: data.bgmMaterialId,
    storyboardMaterialId: data.storyboardMaterialId,
    scenes: data.scenes.map(scene => ({
      id: scene.sceneId,
      order: scene.sceneOrder,
      setting: scene.setting,
      visual: scene.visual,
      script: scene.script,
      narration: scene.narration,
      dialogue: scene.dialogue,
      telop: scene.telop,
      firstFrameMaterialId: scene.firstFrameMaterialId,
      lastFrameMaterialId: scene.lastFrameMaterialId,
      videoMaterialId: scene.videoMaterialId,
      narrationMaterialId: scene.narrationMaterialId,
    })),
  };
}

/**
 * シナリオの全素材をZIPでダウンロード
 *
 * 取得できなかった素材はZIPに入らないが、黙って欠落すると「動画が紐づいているのに
 * 絵コンテとJSONしか入っていない」ように見えるため、失敗した項目名を呼び出し元に返す。
 */
export async function downloadScenarioAsZip(
  data: ScenarioDownloadData,
  onProgress?: ProgressCallback
): Promise<{ failedItems: string[] }> {
  const zip = new JSZip();
  const failedItems: string[] = [];

  // ストーリーボードモードでは絵コンテ画像 1 枚を同梱するので、各シーンの
  // first/last フレーム画像 (絵コンテの各コマ) は冗長なため ZIP に含めない。
  const isStoryboard = data.frameMode === 'storyboard';

  // 総ダウンロード数を計算（動画があるシーンはフレーム画像をスキップ）
  let totalItems = 1; // scenario.json
  if (data.storyboardMaterialId) totalItems++;
  if (data.bgmMaterialId) totalItems++;
  data.scenes.forEach(scene => {
    if (!isStoryboard && scene.firstFrameMaterialId && !scene.videoMaterialId) totalItems++;
    if (!isStoryboard && scene.lastFrameMaterialId && !scene.videoMaterialId) totalItems++;
    if (scene.videoMaterialId) totalItems++;
    if (scene.narrationMaterialId) totalItems++;
  });

  let currentItem = 0;

  const updateProgress = (message: string) => {
    currentItem++;
    onProgress?.({
      current: currentItem,
      total: totalItems,
      message,
    });
  };

  // ファイル名接頭辞（シナリオ名をサニタイズ）
  const namePrefix = data.title.replace(/[\\/:*?"<>|]/g, '_');

  // シナリオJSONを作成 (クリップボードコピーと共通の正本)
  const scenarioJson = buildScenarioExportJson(data);

  zip.file(`${namePrefix}_scenario.json`, JSON.stringify(scenarioJson, null, 2));
  updateProgress('シナリオJSON');

  // ストーリーボードモードの絵コンテ画像（シナリオ全体で 1 枚）
  if (data.storyboardMaterialId) {
    const result = await fetchMaterialBlob(data.storyboardMaterialId);
    if (result) {
      const extension = result.filename.split('.').pop() || 'jpg';
      zip.file(`${namePrefix}_storyboard.${extension}`, result.blob);
    } else {
      failedItems.push('絵コンテ画像');
    }
    updateProgress('絵コンテ画像');
  }

  // 各シーンの素材をダウンロード（動画があるシーンはフレーム画像をスキップ）
  for (const scene of data.scenes) {
    // 最初のフレーム画像（動画がないシーンのみ・ストーリーボードモードでは同梱しない）
    if (!isStoryboard && scene.firstFrameMaterialId && !scene.videoMaterialId) {
      const result = await fetchMaterialBlob(scene.firstFrameMaterialId);
      if (result) {
        const extension = result.filename.split('.').pop() || 'jpg';
        zip.file(`${namePrefix}_scene_${scene.sceneOrder}_first_frame.${extension}`, result.blob);
      } else {
        failedItems.push(`シーン${scene.sceneOrder} 最初のフレーム`);
      }
      updateProgress(`シーン${scene.sceneOrder} - 最初のフレーム`);
    }

    // 最後のフレーム画像（動画がないシーンのみ・ストーリーボードモードでは同梱しない）
    if (!isStoryboard && scene.lastFrameMaterialId && !scene.videoMaterialId) {
      const result = await fetchMaterialBlob(scene.lastFrameMaterialId);
      if (result) {
        const extension = result.filename.split('.').pop() || 'jpg';
        zip.file(`${namePrefix}_scene_${scene.sceneOrder}_last_frame.${extension}`, result.blob);
      } else {
        failedItems.push(`シーン${scene.sceneOrder} 最後のフレーム`);
      }
      updateProgress(`シーン${scene.sceneOrder} - 最後のフレーム`);
    }

    // 動画ファイル
    if (scene.videoMaterialId) {
      const result = await fetchMaterialBlob(scene.videoMaterialId);
      if (result) {
        const extension = result.filename.split('.').pop() || 'mp4';
        zip.file(`${namePrefix}_scene_${scene.sceneOrder}_video.${extension}`, result.blob);
      } else {
        failedItems.push(`シーン${scene.sceneOrder} 動画`);
      }
      updateProgress(`シーン${scene.sceneOrder} - 動画`);
    }

    // ナレーション音声ファイル
    if (scene.narrationMaterialId) {
      const result = await fetchMaterialBlob(scene.narrationMaterialId);
      if (result) {
        const extension = result.filename.split('.').pop() || 'mp3';
        const narrationPart = getNarrationPrefix(scene.narration);
        zip.file(`${namePrefix}_scene_${scene.sceneOrder}${narrationPart}_narration.${extension}`, result.blob);
      } else {
        failedItems.push(`シーン${scene.sceneOrder} ナレーション`);
      }
      updateProgress(`シーン${scene.sceneOrder} - ナレーション`);
    }
  }

  // BGM（シナリオ全体で 1 本）
  if (data.bgmMaterialId) {
    const result = await fetchMaterialBlob(data.bgmMaterialId);
    if (result) {
      const extension = result.filename.split('.').pop() || 'mp3';
      zip.file(`${namePrefix}_bgm.${extension}`, result.blob);
    } else {
      failedItems.push('BGM');
    }
    updateProgress('BGM');
  }

  // ZIPを生成してダウンロード
  onProgress?.({
    current: totalItems,
    total: totalItems,
    message: 'ZIPファイルを生成中...',
  });

  const content = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  // ファイル名をサニタイズ
  const safeTitle = data.title.replace(/[\\/:*?"<>|]/g, '_').substring(0, 50);
  const filename = `${safeTitle}_${data.scenarioId.substring(0, 8)}.zip`;

  saveAs(content, filename);

  return { failedItems };
}
